import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
// Import Shared Components
import { Modal, IconButton, Loader, ErrorComponent } from 'shared-components';
import useWhiteLabeling from 'shared-components/src/hooks/useWhiteLabeling';
// Import Shared Utils
import { get } from 'shared-components/src/utils/http';
// Import Hooks
import {getCardholderSupportEmail, isHRB} from 'shared-components/src/utils/whitelabel';
import useAlert from '../../hooks/useAlert';
// Import Global Components
import Alert from '../../components/Alert';
// Import Local Components
import Transactions from './components/Transactions';
import AccountDetails from './components/AccountDetails';
import HomeHeader from './components/HomeHeader';

export default () => {
  // Set Component States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [alert, setAlert] = useAlert();
  const [cardClosedAlert, setCardClosedAlert] = useState();

  // Set Data States
  const [cardholder, setCardholder] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [currentAccountPrimaryCard, setCurrentAccountPrimaryCard] = useState(
    null
  );
  const [primaryCardStatus, setPrimaryCardStatus] = useState(null);

  const [accountBalance, setAccountBalance] = useState(null);
  const [accountId, setAccountId] = useState(null);

  const intl = useIntl();
  const { callCenterBanner } = useWhiteLabeling();
  const { limitUpdateBanner } = useWhiteLabeling();

  const [limitUpdateModal, setLimitUpdateModal] = useState(false);

  const handleLimitUpdateClick = () => {
    setLimitUpdateModal(true);
  };


  // Set Effects
  useEffect(() => {
    const fetchData = async () => {
      setError(false);

      try {
        // seperate calls
        const cardholderData = await get(
          '/api/v1/cardholder_portal/cardholder'
        );

        setCardholder(cardholderData.data);

        // Set the currentAccount as the primary cardholder account during the initial load
        const account = cardholderData.data.accounts.find(
          acc =>
            acc.processor_reference ===
            cardholderData.data.primary_processor_reference
        );
        const primaryCard = account.cards[0];
        setCurrentAccount(account);

        // NOTE: Set AccountPrimaryCard as the first card in the list
        setCurrentAccountPrimaryCard(primaryCard);
        setPrimaryCardStatus(primaryCard.status);
        // If primary card is closed, display an alert
        if (primaryCard.status === 'closed') {
          setCardClosedAlert({
            for: 'card-closed',
            type: 'info',
          });
        }

        // Set accountId here
        setAccountId(account.id);

        // Fetch Account Balance
        const balanceData = await get(
          `/api/v1/cardholder_portal/cardholder/accounts/${account.id}/balance`
        );

        // Set account balance
        setAccountBalance(balanceData.data);
      } catch (e) {
        setError(true);
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  if (error) return <ErrorComponent />;
  if (loading) return <Loader />;

  const homeHeaderData = {
    lastFourDigits: currentAccountPrimaryCard
      ? currentAccountPrimaryCard.last_four_digits
      : null,
    user: {
      firstName: cardholder.first_name,
      lastName: cardholder.last_name,
    },
    accountId: currentAccount.id,
    cardStatus: primaryCardStatus,
  };

  return (
    <>
      { 
        callCenterBanner && (
          <Alert
            // eslint-disable-next-line dot-notation
            alert={intl.messages['cardholder_support_email']}
            type="warn"
            alertFor="call-center-banner"
            values={{
              cardholder_support_email: getCardholderSupportEmail()
            }}
          />
        ) 
      }
      {
        limitUpdateBanner && isHRB() && (
          <Alert
            // eslint-disable-next-line dot-notation
            alert={intl.messages['program-update-short-alert']}
            type='warn'
            alertFor='program-update-short-alert'
            style={{
              display: 'inline',
            }}
            values={{
              a: chunks => (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLimitUpdateClick();
                  }}
                  style={{
                    textDecoration: 'underline',
                    color: 'inherit',
                  }}
                >
                  {chunks}
                </button>
              ),
              strong: chunks => (
                <strong>
                  {chunks}
                </strong>
              ),
            }}
          />
        )
      }
      {
        limitUpdateModal && isHRB() && (
          <Modal>
            <IconButton
              iconType='x'
              onClick={() => setLimitUpdateModal(false)}
              style={{
                position: 'absolute',
                top: '30px',
                right: '30px'
              }}
            />
            <div style={{ padding: '20px', maxWidth: '600px' }}>
              <h2><strong>{intl.messages['program-update-header']}</strong></h2>
              <br />
              <p>{intl.messages['program-update-body1']}<strong>{intl.messages['program-update-body1-1']}</strong></p>
              <br />
              <h4><strong>{intl.messages['transaction-limits-update-header']}</strong></h4>
              <table style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid black', padding: '5px'}}>{intl.messages['transaction-limits-update-body1']}</th>
                    <th style={{ border: '1px solid black', padding: '5px'}}>{intl.messages['transaction-limits-update-current']}</th>
                    <th style={{ border: '1px solid black', padding: '5px'}}>{intl.messages['transaction-limits-update-new']}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px'}}>{intl.messages['transaction-limits-max-load-amount-per-transaction']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-load-amount-per-transaction-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-load-amount-per-transaction-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-card-balance']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-card-balance-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-card-balance-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-pos']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-pos-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-pos-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-atm']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-atm-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-atm-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-bill-pay-per-txn']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-bill-pay-per-txn-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-bill-pay-per-txn-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-out-funds-transfer-amt']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-out-funds-transfer-amt-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-out-funds-transfer-amt-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-load-amt-external-account']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-load-amt-external-account-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-load-amt-external-account-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-direct-deposit']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-direct-deposit-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-direct-deposit-new']}</td>
                  </tr>
                </tbody>
              </table>
              <br />
              <p>
                {intl.messages['cardholder-agreement-website-1']} <a href="https://hrblockadvantagecard.payaccount.io/">website</a> {intl.messages['cardholder-agreement-website-2']}
              </p>
              <br />
              <p>
                {intl.messages['no-action-needed']}
              </p>
              <br />
              <p>
                {intl.messages['disagreement-option-1']}<strong>1-855-342-6924.</strong>{intl.messages['disagreement-option-2']}
              </p>
              <br />
              <p>
                {intl.messages['program-issuer-1']}
              </p>
              <br />
              <p>{intl.messages['program-mastercard-trademark']}</p>
              <br />
              <p>{intl.messages['interac-trademark']}</p>
            </div>
          </Modal>
        )
      }
      {alert && (
        <Alert
          alert={intl.messages[`${alert.for}-alert`]}
          alertFor={alert.for}
          type={alert.type}
        />
      )}
      <HomeHeader data={homeHeaderData} setAlert={setAlert} />
      {cardClosedAlert && (
        <Alert
          alert={intl.messages[`${cardClosedAlert.for}-alert`]}
          alertFor={cardClosedAlert.for}
          type={cardClosedAlert.type}
        />
      )}
      <AccountDetails
        accountId={accountId}
        account={currentAccount}
        status={currentAccount.status_code}
        expiryDate={currentAccountPrimaryCard.expiry_date}
        accountBalance={accountBalance}
      />
      <Transactions accountId={accountId} accountBalance={accountBalance} />{' '}
    </>
  );
};
